<template>
  <div class="base-quill-editor">
    <div ref="editor"></div>
  </div>
</template>

<script>
import Quill from "quill";
import QuillResize from "quill-resize-module";
import "quill/dist/quill.snow.css";

let resizeModuleRegistered = false;

const defaultToolbar = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline", "strike"],
  [{ color: [] }, { background: [] }],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  ["blockquote", "code-block"],
  ["link", "image"],
  ["clean"],
];

export default {
  name: "BaseQuillEditor",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    useCustomImageHandler: {
      type: Boolean,
      default: false,
    },
    editorOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["update:modelValue", "imageAdded"],
  data() {
    return {
      quill: null,
      changingFromEditor: false,
    };
  },
  watch: {
    modelValue(value) {
      if (!this.quill || this.changingFromEditor) {
        return;
      }

      const nextValue = value || "";
      if (this.quill.root.innerHTML === nextValue) {
        return;
      }

      const selection = this.quill.getSelection();
      this.quill.clipboard.dangerouslyPasteHTML(nextValue);

      if (selection) {
        this.quill.setSelection(selection);
      }
    },
  },
  mounted() {
    if (!resizeModuleRegistered) {
      Quill.register("modules/resize", QuillResize);
      resizeModuleRegistered = true;
    }

    this.quill = new Quill(this.$refs.editor, this.getEditorOptions());

    if (this.modelValue) {
      this.quill.clipboard.dangerouslyPasteHTML(this.modelValue);
    }

    this.quill.on("text-change", () => {
      this.changingFromEditor = true;
      this.$emit("update:modelValue", this.quill.root.innerHTML);
      this.$nextTick(() => {
        this.changingFromEditor = false;
      });
    });
  },
  beforeUnmount() {
    this.quill = null;
  },
  methods: {
    getEditorOptions() {
      const modules = {
        toolbar: defaultToolbar,
        resize: {
          modules: ["Resize", "DisplaySize", "Toolbar"],
        },
        ...(this.editorOptions.modules || {}),
      };

      if (this.useCustomImageHandler) {
        const toolbarConfig = modules.toolbar || defaultToolbar;
        modules.toolbar = {
          container: toolbarConfig.container || toolbarConfig,
          handlers: {
            ...(toolbarConfig.handlers || {}),
            image: this.selectImage,
          },
        };
      }

      return {
        theme: "snow",
        ...this.editorOptions,
        modules,
      };
    },
    selectImage() {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");
      input.click();

      input.onchange = () => {
        const file = input.files && input.files[0];
        if (!file) {
          input.remove();
          return;
        }

        const range = this.quill.getSelection(true);
        const cursorLocation = range ? range.index : this.quill.getLength();
        this.$emit("imageAdded", file, this.quill, cursorLocation, () => {
          input.remove();
        });
      };
    },
  },
};
</script>

<style>
.base-quill-editor .ql-container {
  min-height: 18rem;
  font-size: 1rem;
}

.base-quill-editor .ql-editor {
  min-height: 18rem;
  text-align: left;
}
</style>
